import moment from 'moment';
import { useQuery } from 'react-query';
import type { QueryOptions, UseQueryResult } from 'react-query';

import { useRequest } from '../common';
import { DATE_RANGE_PRESETS } from './constants';
import type { FiltersType, NotificationQueryResponse } from './types';

const QUERY_PARAMS = {
  EVENT_TIME_RANGE: 'event_time_range',
  EVENT_TIME_ON: 'event_time_on',
};

export const getNotificationListQueryKeyPrefix = () => ['notifications'];

/**
 * Helper function to retrieve url params turning on the presence, or absence, of dateRange property. Significance being whether the client is using generic filter range (e.g., "Last 7 days" ) or custom
 */
const getEventTimeParams = (
  dateMax: FiltersType['dateMax'],
  dateMin: FiltersType['dateMin'],
  dateRange: FiltersType['dateRange'],
): { [key: string]: string } => {
  if (dateRange) {
    switch (dateRange) {
      case DATE_RANGE_PRESETS.NONE:
        return {};
      case DATE_RANGE_PRESETS.TODAY:
        return {
          [QUERY_PARAMS.EVENT_TIME_RANGE]: `${moment()
            .startOf('day')
            .toISOString()},${moment().endOf('day').toISOString()}`,
        };
      case DATE_RANGE_PRESETS.LAST_SEVEN_DAYS:
        return {
          [QUERY_PARAMS.EVENT_TIME_RANGE]: `${moment()
            .startOf('day')
            .subtract(7, 'days')
            .toISOString()},${moment().endOf('day').toISOString()}`,
        };
      case DATE_RANGE_PRESETS.LAST_THIRTY_DAYS:
        return {
          [QUERY_PARAMS.EVENT_TIME_RANGE]: `${moment()
            .startOf('day')
            .subtract(30, 'days')
            .toISOString()},${moment().endOf('day').toISOString()}`,
        };
      case DATE_RANGE_PRESETS.LAST_SIX_MONTHS:
        return {
          [QUERY_PARAMS.EVENT_TIME_RANGE]: `${moment()
            .startOf('day')
            .subtract(6, 'months')
            .toISOString()},${moment().endOf('day').toISOString()}`,
        };
      default:
        // should never be here.
        return {};
    }
  }
  return dateMax !== undefined && dateMin !== undefined
    ? {
        [QUERY_PARAMS.EVENT_TIME_RANGE]: `${dateMin},${dateMax}`,
      }
    : dateMin !== undefined
    ? {
        [QUERY_PARAMS.EVENT_TIME_ON]: dateMin,
      }
    : {};
};

export const useNotificationsQuery = (
  query: FiltersType,
  options?: QueryOptions,
): UseQueryResult<NotificationQueryResponse> => {
  const {
    searchTerm,
    organizationIds,
    dateMin,
    dateMax,
    page,
    filterOutAcknowledged,
    dateRange,
  } = query;

  const request = useRequest();

  const maybeInclude = (key: string, value: any): object =>
    value === undefined ? {} : { [key]: value };

  const eventTimeParams: { [key: string]: string } = getEventTimeParams(
    dateMax,
    dateMin,
    dateRange,
  );

  const newQuery = {
    ...maybeInclude('search', searchTerm),
    ...maybeInclude('organization_in', organizationIds),
    ...maybeInclude('page', page?.toString()),
    ...maybeInclude('filter_out_acknowledged', filterOutAcknowledged),
    ...eventTimeParams,
  };

  return useQuery({
    queryKey: ['notifications', newQuery],
    queryFn: () =>
      request(`/notifications`, {
        query: newQuery,
      }),
    keepPreviousData: true,
    ...options,
  });
};
